import React, { FC, ReactNode, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ActionBar from './ActionBar';

export interface TableBulkAction<TInput = Record<string, any>> {
  id: string;
  label: string;
  initialInput: TInput;
  renderAdditionalElements?: (props: {
    value: TInput;
    onChange: (value: TInput) => void;
    disabled: boolean;
  }) => ReactNode;
  onExecute: (selectedIds: string[], input: TInput) => void | Promise<void>;
}

interface Props {
  selectedIds: string[];
  visibleIds?: string[];
  onToggleSelectAllVisible?: (checked: boolean) => void;
  actions: TableBulkAction[];
  disabled?: boolean;
  actionSelectLabel?: string;
  executeLabel?: string;
  selectedText?: string;
}

const TableBulkActionBar: FC<Props> = ({
  selectedIds,
  visibleIds = [],
  onToggleSelectAllVisible,
  actions,
  disabled = false,
  actionSelectLabel = 'Action',
  executeLabel = 'Apply',
  selectedText = 'selected',
}) => {
  const theme = useTheme();
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const [inputsByActionId, setInputsByActionId] = useState<Record<string, any>>(() => {
    const map: Record<string, any> = {};
    actions.forEach((action) => {
      map[action.id] = action.initialInput;
    });
    return map;
  });

  const currentAction = useMemo(
    () => actions.find((action) => action.id === selectedActionId),
    [actions, selectedActionId]
  );

  const selectedVisibleCount = useMemo(
    () => visibleIds.filter((id) => selectedIds.includes(id)).length,
    [visibleIds, selectedIds]
  );

  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const selectAllIndeterminate = selectedVisibleCount > 0 && !allVisibleSelected;

  const handleExecute = async () => {
    if (!currentAction || selectedIds.length === 0) {
      return;
    }

    const input = inputsByActionId[currentAction.id] ?? currentAction.initialInput;
    await currentAction.onExecute(selectedIds, input);
  };

  if (actions.length === 0) {
    return null;
  }

  if (visibleIds.length === 0) {
    return null;
  }

  return (
    <ActionBar
      sx={{
        height: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        mt: 1,
        mb: 1,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ width: '100%' }}
      >
        {onToggleSelectAllVisible && (
          <FormControlLabel
            control={
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={selectAllIndeterminate}
                onChange={() => onToggleSelectAllVisible(!allVisibleSelected)}
                disabled={disabled || visibleIds.length === 0}
              />
            }
            label=""
          />
        )}
        <Typography
          sx={{
            pr: theme.spacing(2),
          }}
          variant="body2"
        >
          {selectedIds.length} {selectedText}
        </Typography>
        <FormControl
          size="small"
          sx={{ minWidth: 220 }}
          disabled={disabled}
        >
          <InputLabel id="bulk-action-select-label">{actionSelectLabel}</InputLabel>
          <Select
            labelId="bulk-action-select-label"
            value={selectedActionId}
            label={actionSelectLabel}
            onChange={(event: SelectChangeEvent) => setSelectedActionId(event.target.value)}
          >
            <MenuItem value="">
              <em>-</em>
            </MenuItem>
            {actions.map((action) => (
              <MenuItem
                key={action.id}
                value={action.id}
              >
                {action.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {currentAction?.renderAdditionalElements?.({
          value: inputsByActionId[currentAction.id] ?? currentAction.initialInput,
          onChange: (value) =>
            setInputsByActionId((prevState) => ({
              ...prevState,
              [currentAction.id]: value,
            })),
          disabled,
        })}
        <Button
          variant="contained"
          disabled={disabled || selectedIds.length === 0 || !currentAction}
          onClick={handleExecute}
        >
          {executeLabel}
        </Button>
      </Stack>
    </ActionBar>
  );
};

export default TableBulkActionBar;
